import React, { useEffect, useState } from "react";
import Header from "./Header";
import Footer from "./Footer";
import config from "../../config";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const SHOPIFY_SCOPES =
  "write_assigned_fulfillment_orders, read_assigned_fulfillment_orders, write_draft_orders, read_draft_orders, write_fulfillments, read_fulfillments, write_merchant_managed_fulfillment_orders, read_merchant_managed_fulfillment_orders, write_inventory, read_inventory, write_orders, read_orders, write_products, read_products, write_product_listings, read_product_listings, write_product_feeds, read_product_feeds";

const ShopifyConnect = () => {
  const navigate = useNavigate();

  const [storeId, setStoreId] = useState("");
  const [installUrl, setInstallUrl] = useState("");
  const [shop, setShop] = useState("");

  const [installLoading, setInstallLoading] = useState(false);

  const [connecting, setConnecting] = useState(false);

  /*
   * =========================================================
   * CREATE STORE ID + READ SHOP CALLBACK
   * =========================================================
   */

  useEffect(() => {
    const randomStoreId = String(
      Math.floor(1000000000 + Math.random() * 9000000000)
    );

    setStoreId(randomStoreId);

    const params = new URLSearchParams(window.location.search);

    const shopParam = params.get("shop");

    if (shopParam) {
      setShop(shopParam);

      handleFinalConnect(shopParam, randomStoreId);
    }
  }, []);

  /*
   * =========================================================
   * COPY TO CLIPBOARD
   * =========================================================
   */

  const handleCopy = async (value, label = "Value") => {
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);

      Swal.fire({
        toast: true,
        position: "bottom-end",
        icon: "success",
        title: `${label} copied`,
        showConfirmButton: false,
        timer: 1800,
      });
    } catch (error) {
      console.error("Clipboard error:", error);
    }
  };

  /*
   * =========================================================
   * INSTALL SHOPIFY APP
   * =========================================================
   */

  const handleInstall = async () => {
    const value = installUrl.trim();

    if (!value) {
      Swal.fire({
        icon: "warning",
        title: "Install URL required",
        text: "Please paste the Shopify Install URL generated from your Shopify dashboard.",
      });

      return;
    }

    try {
      setInstallLoading(true);

      const response = await axios.post(
        "https://backend.kalpcargo.com/auth/preConnect",
        {
          install_url: value,
        },
        {
          headers: config.headers,
        }
      );

      if (!response.data) {
        throw new Error("Invalid response from Shopify.");
      }

      window.location.href = response.data;
    } catch (error) {
      console.error("Shopify preConnect error:", error);

      Swal.fire({
        icon: "error",
        title: "Unable to continue",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to start Shopify installation.",
      });
    } finally {
      setInstallLoading(false);
    }
  };

  /*
   * =========================================================
   * FINAL CONNECT
   * =========================================================
   */

  const handleFinalConnect = async (shopName, generatedStoreId) => {
    const userDetail = localStorage.getItem("ship_rocket_user");

    /*
     * Not logged in
     */
    if (!userDetail) {
      window.location.href = `/login?redirect=shopify-connect&shop=${encodeURIComponent(
        shopName
      )}`;

      return;
    }

    let userData = {};

    try {
      userData = JSON.parse(userDetail);
    } catch (error) {
      console.error("Invalid user data:", error);

      window.location.href = `/login?redirect=shopify-connect&shop=${encodeURIComponent(
        shopName
      )}`;

      return;
    }

    try {
      setConnecting(true);

      const response = await axios.post(
        "https://backend.kalpcargo.com/auth/finalConnect",
        {
          shop: shopName,
          store_id: generatedStoreId,
          customer_id: userData.customer_id,
        },
        {
          headers: config.headers,
        }
      );

      if (response.data?.status === "success") {
        window.location.href = "/channel_list?status=success";
      } else if (response.data?.status === "already_connected") {
        window.location.href = "/channel_list?status=already_connected";
      } else {
        Swal.fire({
          icon: "error",
          title: "Integration failed",
          text:
            response.data?.message || "Unable to complete Shopify integration.",
        });
      }
    } catch (error) {
      console.error("Shopify finalConnect error:", error);

      Swal.fire({
        icon: "error",
        title: "Integration failed",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to complete Shopify integration.",
      });
    } finally {
      setConnecting(false);
    }
  };

  /*
   * =========================================================
   * RENDER
   * =========================================================
   */

  return (
    <div>
      <Header>
        <div className="content">
          {/* =================================================
              TOP HEADER
              ================================================= */}

          <div className="ol-header ig-top">
            <div className="ol-hd">
              <button
                type="button"
                className="ol-back"
                title="Go back"
                onClick={() => navigate(-1)}
              >
                <i className="bi bi-arrow-left"></i>
              </button>
            </div>

            <div className="ol-actions">
              <button
                type="button"
                className="ol-btn"
                onClick={() => navigate("/channel_list")}
              >
                <i className="bi bi-plug"></i>
                Integrations
              </button>

              <a href="/developer_apis" className="ol-btn ghost">
                <i className="bi bi-book"></i>
                API Docs
              </a>
            </div>
          </div>

          {/* =================================================
              HERO
              ================================================= */}

          <div className="sc-hero">
            <div className="sc-hero-left">
              <div className="sc-brand">
                <div className="sc-shopify-logo">
                  <img src="assets/img/shopify.png" alt="Shopify" />
                </div>

                <div>
                  <span className="sc-eyebrow">E-commerce Integration</span>

                  <h1>Connect Shopify</h1>
                </div>
              </div>

              <p className="sc-hero-desc">
                Connect your Shopify store with KalpCargo to automatically sync
                orders and manage shipments from one place.
              </p>

              <div className="sc-trust-row">
                <span>
                  <i className="bi bi-shield-check"></i>
                  Secure connection
                </span>

                <span>
                  <i className="bi bi-arrow-repeat"></i>
                  Automatic sync
                </span>

                <span>
                  <i className="bi bi-box-seam"></i>
                  Shipment updates
                </span>
              </div>
            </div>

            <div className="sc-hero-status">
              {connecting ? (
                <>
                  <span className="sc-status-icon loading">
                    <span className="spinner-border spinner-border-sm"></span>
                  </span>

                  <div>
                    <b>Connecting...</b>

                    <small>Completing Shopify authorization</small>
                  </div>
                </>
              ) : (
                <>
                  <span className="sc-status-icon">
                    <i className="bi bi-plug"></i>
                  </span>

                  <div>
                    <b>One-time setup</b>

                    <small>Follow the steps below</small>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* =================================================
              MAIN CONTENT
              ================================================= */}

          <div className="sc-layout">
            <div className="sc-main">
              {/* =============================================
                  STEP 1
                  ============================================= */}

              <section className="sc-step card-base">
                <div className="sc-step-head">
                  <span className="sc-step-number">1</span>

                  <div>
                    <h2>Login to Shopify Partner Dashboard</h2>

                    <p>Start by opening your Shopify Partner account.</p>
                  </div>

                  <span className="sc-step-check">
                    <i className="bi bi-person-check"></i>
                  </span>
                </div>

                <div className="sc-step-body">
                  <div className="sc-instruction-card">
                    <div className="sc-instruction-icon">
                      <i className="bi bi-box-arrow-up-right"></i>
                    </div>

                    <div>
                      <b>Open Shopify Partners</b>

                      <span>Log in using your Shopify account.</span>
                    </div>

                    <a
                      href="https://partners.shopify.com"
                      target="_blank"
                      rel="noreferrer"
                      className="ol-btn"
                    >
                      Open Shopify
                      <i className="bi bi-arrow-up-right"></i>
                    </a>
                  </div>
                </div>
              </section>

              {/* =============================================
                  STEP 2
                  ============================================= */}

              <section className="sc-step card-base">
                <div className="sc-step-head">
                  <span className="sc-step-number">2</span>

                  <div>
                    <h2>Create a Custom App</h2>

                    <p>
                      Create the application that KalpCargo will use to connect
                      to your store.
                    </p>
                  </div>
                </div>

                <div className="sc-step-body">
                  <ol className="sc-check-list">
                    <li>
                      Go to <b>App Distribution</b> → <b>All apps</b>
                    </li>

                    <li>
                      Click <b>Visit Dev dashboard</b>
                    </li>

                    <li>
                      Click <b>Create app</b>
                    </li>

                    <li>
                      Enter an app name, for example:
                      <strong>KalpCargo Integration</strong>
                    </li>

                    <li>
                      In <b>App URL</b>, enter:
                    </li>
                  </ol>

                  <div className="sc-code-copy">
                    <code>https://backend.kalpcargo.com/auth/callback</code>

                    <button
                      type="button"
                      onClick={() =>
                        handleCopy(
                          "https://backend.kalpcargo.com/auth/callback",
                          "App URL"
                        )
                      }
                      title="Copy"
                    >
                      <i className="bi bi-copy"></i>
                    </button>
                  </div>

                  <ol className="sc-check-list" start="6">
                    <li>
                      Uncheck <b>Embed app in Shopify admin</b>
                    </li>

                    <li>
                      Go to <b>Access → Scopes</b>
                    </li>

                    <li>Add the following scopes:</li>
                  </ol>

                  <div className="sc-scope-box">
                    <code>{SHOPIFY_SCOPES}</code>

                    <button
                      type="button"
                      onClick={() => handleCopy(SHOPIFY_SCOPES, "Scopes")}
                      title="Copy scopes"
                    >
                      <i className="bi bi-copy"></i>
                    </button>
                  </div>

                  <div className="sc-tip">
                    <i className="bi bi-info-circle"></i>

                    <span>
                      After configuring the scopes, scroll down and click{" "}
                      <b>Release</b>.
                    </span>
                  </div>
                </div>
              </section>

              {/* =============================================
                  STEP 3
                  ============================================= */}

              <section className="sc-step card-base">
                <div className="sc-step-head">
                  <span className="sc-step-number">3</span>

                  <div>
                    <h2>Configure App Distribution</h2>

                    <p>
                      Create a custom distribution link for your Shopify store.
                    </p>
                  </div>
                </div>

                <div className="sc-step-body">
                  <ol className="sc-check-list">
                    <li>Open the app you just created.</li>

                    <li>
                      Click <b>Home</b>.
                    </li>

                    <li>
                      Go to <b>Distribution</b> →{" "}
                      <b>Select distribution method</b>
                    </li>

                    <li>
                      Select <b>Custom Distribution</b>
                    </li>

                    <li>
                      Click <b>Select</b>.
                    </li>

                    <li>
                      Confirm <b>Select custom distribution</b>
                    </li>

                    <li>Enter your Shopify store URL:</li>
                  </ol>

                  <div className="sc-store-example">
                    <i className="bi bi-shop"></i>

                    <code>yourstore.myshopify.com</code>
                  </div>

                  <ol className="sc-check-list" start="8">
                    <li>
                      Click <b>Generate Link</b>
                    </li>

                    <li>
                      Shopify will generate an <b>Install URL</b>.
                    </li>
                  </ol>
                </div>
              </section>

              {/* =============================================
                  STEP 4 INSTALL
                  ============================================= */}

              <section className="sc-step card-base sc-install-step">
                <div className="sc-step-head">
                  <span className="sc-step-number">4</span>

                  <div>
                    <h2>Install Shopify App</h2>

                    <p>Paste the Install URL generated by Shopify below.</p>
                  </div>

                  <span className="sc-step-check install">
                    <i className="bi bi-arrow-right-circle"></i>
                  </span>
                </div>

                <div className="sc-step-body">
                  <label className="sc-field-label">Shopify Install URL</label>

                  <div className="sc-install-input">
                    <i className="bi bi-link-45deg"></i>

                    <input
                      type="text"
                      value={installUrl}
                      onChange={(e) => setInstallUrl(e.target.value)}
                      placeholder="https://admin.shopify.com/oauth/install_custom_app?client_id=..."
                      autoComplete="off"
                    />

                    {installUrl && (
                      <button
                        type="button"
                        onClick={() => setInstallUrl("")}
                        title="Clear"
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    )}
                  </div>

                  <button
                    type="button"
                    className="sc-install-btn"
                    disabled={installLoading || !installUrl.trim()}
                    onClick={handleInstall}
                  >
                    {installLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm"></span>
                        Opening Shopify...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-shop"></i>
                        Install Shopify App
                        <i className="bi bi-arrow-right"></i>
                      </>
                    )}
                  </button>

                  <div className="sc-install-note">
                    <i className="bi bi-shield-check"></i>

                    <div>
                      <b>Secure installation</b>

                      <span>
                        You'll be redirected to Shopify to approve the
                        connection. KalpCargo does not ask you to share your
                        Admin API password.
                      </span>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            {/* ===============================================
                RIGHT SIDEBAR
                =============================================== */}

            <aside className="sc-sidebar">
              {/* QUICK SUMMARY */}

              <div className="sc-side-card card-base">
                <div className="sc-side-title">
                  <i className="bi bi-list-check"></i>
                  Setup checklist
                </div>

                <div className="sc-progress-list">
                  <div className="sc-progress-item done">
                    <span>1</span>
                    <div>
                      <b>Shopify account</b>
                      <small>Partner Dashboard</small>
                    </div>
                  </div>

                  <div className="sc-progress-line"></div>

                  <div className="sc-progress-item">
                    <span>2</span>
                    <div>
                      <b>Custom App</b>
                      <small>Create and configure</small>
                    </div>
                  </div>

                  <div className="sc-progress-line"></div>

                  <div className="sc-progress-item">
                    <span>3</span>
                    <div>
                      <b>Distribution</b>
                      <small>Generate install link</small>
                    </div>
                  </div>

                  <div className="sc-progress-line"></div>

                  <div className="sc-progress-item">
                    <span>4</span>
                    <div>
                      <b>Install</b>
                      <small>Connect your store</small>
                    </div>
                  </div>
                </div>
              </div>

              {/* IMPORTANT INFO */}

              <div className="sc-side-card card-base">
                <div className="sc-side-title">
                  <i className="bi bi-shield-lock"></i>
                  Important
                </div>

                <ul className="sc-info-list">
                  <li>
                    <i className="bi bi-check-circle"></i>
                    No API key or secret needs to be shared with KalpCargo.
                  </li>

                  <li>
                    <i className="bi bi-check-circle"></i>
                    No Admin API token is required.
                  </li>

                  <li>
                    <i className="bi bi-check-circle"></i>
                    Orders are synced using Shopify webhooks.
                  </li>
                </ul>
              </div>

              {/* STORE DETAILS */}

              {shop && (
                <div className="sc-side-card card-base">
                  <div className="sc-side-title">
                    <i className="bi bi-shop"></i>
                    Store
                  </div>

                  <div className="sc-store-value">{shop}</div>

                  {storeId && (
                    <div className="sc-store-id">Store ID: #{storeId}</div>
                  )}
                </div>
              )}
            </aside>
          </div>
        </div>
      </Header>

      <Footer />
    </div>
  );
};

export default ShopifyConnect;
